import React, { Suspense, lazy, useState } from "react";

import PageHead from "../../components/PageHead";
import ImageAdStudio from "../../../components/ImageAdStudio";
import CreativeWorkspace from "../../../components/CreativeWorkspace";
import * as api from "./api";
import CreativeActions from "./CreativeActions";
import SessionDelete from "./SessionDelete";
import CustomSessions from "./CustomSessions";
import AutonomousSessionScreen from "./AutonomousSessionScreen";
import CustomSessionScreen from "./CustomSessionScreen";
import OfferPicker from "./OfferPicker";
import { Async, Kpi, KpiRow, Panel } from "./panels";
import { useEndpoint, useSessionSelection } from "./useOffer";

/**
 * LAZY, AND NOT AS AN OPTIMISATION.
 *
 * `CreativeAnalyzer.jsx` imports `useAuth` from `../App`, so a static import
 * here would put v1's 4,900-line root module into the static graph of every
 * consumer of this tab — and `App.js` runs side effects at module scope
 * (`axios.defaults.withCredentials = true`, App.js:66). V2Routes' own header
 * says the `../App` cycle is safe "because it is dereferenced at render time,
 * not at module evaluation"; a static import here would break exactly that
 * property. Measured, not assumed: adding it statically made
 * `workstation.test.jsx` and `customSessions.test.jsx` fail to run at all.
 *
 * `lazy` restores the property — this module is resolved when the operator
 * opens the analyzer, not when the Workstation loads.
 *
 * It costs the operator nothing. Verified against a real `CI=true npx craco
 * build`, not assumed: the build still emits ONE js chunk, because `App.js`
 * imports `CreativeAnalyzer` statically for v1's `/analyzer` route, so webpack
 * has nowhere to split it to. The code is already in the page, `import()`
 * resolves from the loaded bundle, and the Suspense fallback below is a
 * correctness requirement rather than a loading state anyone will see.
 */
const CreativeAnalyzer = lazy(() => import("../../../components/CreativeAnalyzer"));

/**
 * Workstation · Ads & Creative (§4, tab 5 of 8).
 *
 * `design/system/audits/workstation-tabs.md` §5
 * ==============================================
 * The deepest-covered tab of the eight — `creative_engine.py` alone
 * contributes 87 routes. This tab does not attempt to surface all of them;
 * per R2/R4 it wires the offer-scoped inventory and session views that exist
 * today, and points to the existing full surfaces (Creative Hub, the
 * Creative Session itself) for everything downstream of "which productions
 * and sessions does this offer have":
 *
 *   GET /api/offers/{offer_id}/productions   creative_engine.py:709
 *   GET /api/creative/sessions?offer_id=     creative_engine.py:1483
 *   GET /api/creative-engine/stats           creative_engine.py:1947 (account-wide)
 *
 * The two actions (open the session library / start a new session) are the
 * SAME component `Overview` already mounts — `CreativeActions.jsx` — reused
 * rather than re-expressed, for the same reason its own header comment gives:
 * one copy of the CCS-cutover branch, not two that can drift.
 *
 * THE CREATIVE ANALYZER LIVES HERE TOO — issue #199
 * =================================================
 * Founder request, placement approved: Workstation → Ads & Creative. This is
 * a RELOCATION, not a rebuild. `components/CreativeAnalyzer.jsx` already works
 * and is already served at v1's `/analyzer`; it is mounted here as-is.
 *
 * IT IS MOUNTED UNMODIFIED, AND THAT IS THE POINT
 * -----------------------------------------------
 * v1 is not being retired and ABH stays on v1 indefinitely, so v1's
 * `/analyzer` must keep rendering exactly what it renders today. The component
 * therefore gains no prop, no flag and no branch for this surface: the back
 * affordance is rendered by THIS file, outside it, the same way the Custom
 * Creative Sessions screen already does it. `App.js:4818`'s route and this
 * mount point render one and the same component, and nothing inside the
 * component can tell which one it is under.
 *
 * NO OFFER IS HANDED TO IT, DELIBERATELY
 * --------------------------------------
 * Every other panel on this tab is scoped by the Workstation's selected offer.
 * The analyzer is not, and must not be: it resolves its own AFFILIATE-scoped
 * offers from `GET /api/analyzer/offers` (`backend/routers/analyzer.py`) and
 * derives the offer from the first segment of the validated asset name
 * ([OFFER-ID]-[CHANNEL]-…). Feeding it this tab's `selectedId` would mean
 * changing the component to accept one, which is the change v1 cannot have.
 * So its entry sits in the tab's action row rather than inside the
 * offer-scoped grid, and it is reachable with no offer selected.
 *
 * NO CLIENT GATE (R5), NO BACKEND (R4)
 * ------------------------------------
 * `/api/analyzer/*` is gated server-side and the component already surfaces
 * that refusal honestly ("Your AFFILIATE access may not be provisioned").
 * Nothing here re-checks a role, and no endpoint was added for this.
 *
 * KNOWN, PRE-EXISTING VISUAL DRIFT (not introduced by this mount)
 * --------------------------------------------------------------
 * Inside the v1.1 shell this subtree sits under `.cipher-ui`, where
 * `shell/cipher-system.css:151` declares
 * `.cipher-ui button { background: none; border: 0 }`.
 *
 * MEASURED IN A BROWSER, not inferred from the selectors: against a real
 * production build served locally, `getComputedStyle` on this component's mode
 * toggle reports its Tailwind BACKGROUNDS intact (rgba(0,240,255,.15) /
 * rgb(19,56,66)) and its BORDER-WIDTH flattened to 0px. So the drift is
 * borders only, not backgrounds — the surface reads correctly, it just loses
 * the 1px outlines it has at v1's `/analyzer`.
 *
 * None of this is specific to the analyzer: `ImageAdStudio` and
 * `CreativeWorkspace`, mounted the same way above, sit under the same rule.
 * The only correct fix is shell-wide, in `cipher-system.css`, for all five
 * areas at once — so it is tracked separately rather than patched per
 * component here.
 */

/* RAW STATUS CODES WERE BEING SHOWN TO THE OPERATOR. `p.status` is an internal
 * enum and was rendered verbatim — "BRIEF_GENERATED", "VEO_SUBMITTED". The
 * vocabulary below is not invented: it is every value present in the live
 * `productions` collection, counted 2026-09-11 —
 *   BRIEF_GENERATED 143 · VEO_SUBMITTED 21 · COMPLETED 8 · AWAITING_EDITOR 7 ·
 *   FAILED 4
 * plus the remaining members of `ProductionStatus`
 * (`production_routing.py:577`) so a value that appears later still reads.
 *
 * VEO_SUBMITTED IS NOT RELABELLED AWAY. Veo is retired, and 21 live rows still
 * carry that status. Hiding it behind a friendly phrase would hide the fact
 * that those productions were handed to a system that no longer runs, so it is
 * named as what it is. The Founder should see that, not a euphemism.
 *
 * An UNKNOWN value is shown as itself rather than guessed at, and marked
 * unknown — an unrecognised status must not be dressed up as a recognised one.
 */
const STATUS_LABEL = {
  PENDING_BRIEF: "Awaiting brief",
  BRIEF_GENERATED: "Brief ready",
  PENDING_APPROVAL: "Awaiting approval",
  APPROVED: "Approved",
  IN_PRODUCTION: "In production",
  AWAITING_EDITOR: "With the editor",
  CHANGES_REQUESTED: "Changes requested",
  COMPLETED: "Completed",
  FAILED: "Failed",
  VEO_SUBMITTED: "Sent to Veo (retired system)",
  // Creative SESSION statuses are a different, lower-case vocabulary and the
  // same row renderer shows them. Live values, counted 2026-09-11:
  // active 105 · completed 22. Kept in one map because one component renders
  // both; the values cannot collide (these are lower-case, those upper).
  active: "In progress",
  completed: "Completed",
};

const STATUS_TONE = {
  completed: "ok",
  COMPLETED: "ok",
  APPROVED: "ok",
  IN_PRODUCTION: "ok",
  FAILED: "crit",
  VEO_SUBMITTED: "warn",
  CHANGES_REQUESTED: "warn",
};

export function statusLabel(raw) {
  if (!raw) return { text: "—", tone: undefined, title: "no status on this record" };
  const known = STATUS_LABEL[raw];
  return known
    ? { text: known, tone: STATUS_TONE[raw], title: raw }
    : { text: raw, tone: "unknown", title: `${raw} — not a status this screen recognises` };
}

// A production that is neither finished nor dead can have its finished video
// returned and attached. Mirrors the backend's blocked-status set
// (`ops.py:_MARK_COMPLETE_BLOCKED_STATUSES`).
const COMPLETION_BLOCKED = new Set(["COMPLETED", "FAILED", "PENDING_BRIEF"]);

/**
 * The finished-video return action (issue #108). On a production that is still
 * with the editor, the operator pastes the finished video's Google Drive file
 * URL; completing writes it to the production and registers the creative — the
 * one and only point a video/autonomous creative is registered. Nothing here
 * activates, publishes, or spends.
 */
function MarkCompleteAction({ p, onCompleted }) {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    const value = url.trim();
    if (!value || busy) return;
    setBusy(true);
    setError(null);
    const res = await api.markProductionComplete(p.production_id, value);
    setBusy(false);
    if (!res.ok) {
      setError(res.error || "Could not mark complete.");
      return;
    }
    setOpen(false);
    setUrl("");
    if (onCompleted) onCompleted();
  };

  if (!open) {
    return (
      <button type="button" className="ws-linkbtn" onClick={() => setOpen(true)}>
        Mark complete + attach finished video
      </button>
    );
  }
  return (
    <form className="ws-inline-form" onSubmit={submit}>
      <input
        type="url"
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        placeholder="Finished video Google Drive file URL"
        aria-label="Finished video Google Drive file URL"
        disabled={busy}
      />
      <button type="submit" disabled={busy || !url.trim()}>
        {busy ? "Saving…" : "Complete"}
      </button>
      <button type="button" onClick={() => { setOpen(false); setError(null); }} disabled={busy}>
        Cancel
      </button>
      {error ? <span className="ws-inline-error" role="alert">{error}</span> : null}
    </form>
  );
}

function ProductionRow({ p, onCompleted }) {
  const st = statusLabel(p.status);
  const canComplete = p.production_id && !COMPLETION_BLOCKED.has(p.status);
  return (
    <li className="ws-understanding-item">
      <span>
        {p.creative_identifier || p.production_id}
        {p.creative_type ? ` · ${p.creative_type}` : ""}
      </span>
      <span className="ws-tag" data-tone={st.tone} title={st.title}>{st.text}</span>
      {p.output_url ? (
        <a href={p.output_url} target="_blank" rel="noopener noreferrer">
          Open finished video
        </a>
      ) : null}
      {canComplete ? <MarkCompleteAction p={p} onCompleted={onCompleted} /> : null}
    </li>
  );
}

function Productions({ offerId }) {
  // `reloadKey` lets a completed production refresh the list without a page
  // reload — `useEndpoint` re-runs when its deps change.
  const [reloadKey, setReloadKey] = useState(0);
  const productions = useEndpoint(
    () => api.getOfferProductions(offerId),
    [offerId, reloadKey],
    { skip: !offerId },
  );

  return (
    <Panel
      title="Productions"
      subtitle="Every ad/production Cipher has generated for this offer."
    >
      <Async result={productions} empty="No productions for this offer yet.">
        {(data) => {
          const rows = data?.productions || [];
          if (rows.length === 0) {
            return <div className="state ws-state">No productions for this offer yet.</div>;
          }
          return (
            <ul className="ws-list">
              {rows.slice(0, 20).map((p, i) => (
                <ProductionRow
                  key={p.production_id || i}
                  p={p}
                  onCompleted={() => setReloadKey((k) => k + 1)}
                />
              ))}
            </ul>
          );
        }}
      </Async>
    </Panel>
  );
}

function Sessions({ offerId, sessions, onOpenImageAds, onOpenWorkspace }) {
  // Deleted rows leave the list at once. `useEndpoint` has no refetch and
  // giving it one would change a hook the whole Workstation shares, which is
  // more blast radius than this needs — so the panel remembers what it deleted.
  // A cascade returns every id it removed, so a whole set disappears together.
  const [gone, setGone] = useState([]);
  const forget = (data) => setGone((prev) => [...prev, ...(data?.session_ids || [])]);

  return (
    <Panel
      title="Creative sessions"
      subtitle="Open an existing session, or start a new one."
      actions={
        <CreativeActions offerId={offerId} sessions={sessions} onOpenImageAds={onOpenImageAds} onOpenWorkspace={onOpenWorkspace} />
      }
    >
      <Async result={sessions} empty="No creative sessions for this offer yet.">
        {(data) => {
          const rows = (data?.sessions || [])
            .filter((s) => s.offer_id === offerId)
            .filter((s) => !gone.includes(s.session_id));
          if (rows.length === 0) {
            return (
              <div className="state ws-state">
                No creative sessions for this offer yet — use the action above to start one.
              </div>
            );
          }
          return (
            <ul className="ws-list">
              {rows.slice(0, 20).map((s) => (
                <li key={s.session_id} className="ws-session-row">
                  <span>Session {s.session_number ?? s.session_id}</span>
                  {/* A track says so, so an operator knows before they click
                      Delete that this one belongs to a set. */}
                  {Number(s.track_total || 0) > 1 ? (
                    <span className="ws-tag" data-testid={`ws-session-track-${s.session_id}`}>
                      track {s.track_index} of {s.track_total}
                    </span>
                  ) : null}
                  {(() => {
                    const st = statusLabel(s.status);
                    return <span className="ws-tag" data-tone={st.tone} title={st.title}>{st.text}</span>;
                  })()}
                  {/* Founder ruling 2026-09-09: a person can delete a session
                      they created. The BACKEND decides who may — this only
                      renders its answer. */}
                  <SessionDelete session={s} onDeleted={forget} />
                </li>
              ))}
            </ul>
          );
        }}
      </Async>
    </Panel>
  );
}

function EngineStats() {
  const stats = useEndpoint(() => api.getCreativeEngineStats(), []);
  return (
    <Panel
      title="Creative engine, account-wide"
      subtitle="Not offer-scoped — the engine's overall pipeline. `creative_engine.py:1947`."
    >
      <Async result={stats} empty="No engine stats recorded yet.">
        {(data) => {
          const production = data?.pipeline?.production || {};
          return (
            <KpiRow>
              <Kpi label="Approved" value={production.approved ?? null} />
              <Kpi label="In production" value={production.in_production ?? null} />
              <Kpi label="Completed" value={production.completed ?? null} />
              <Kpi label="Pending approval" value={production.pending_approval ?? null} />
            </KpiRow>
          );
        }}
      </Async>
    </Panel>
  );
}

export default function AdsCreative({ selection }) {
  const { offers, selected, selectedId, select, error } = selection;
  // `?session=` — the convention the Founder ratified on 2026-09-08, matching
  // `?offer=`. See `useOffer.js`; the shell has no route below the tab, so the
  // query string is where a live object goes.
  const { sessionId, open, close } = useSessionSelection();
  // `&new=1` is how the start flow marks a session that has just been created —
  // the same marker v1's wizard reads, so the two agree about what "new" means.
  const isNew = (() => {
    try {
      return new URLSearchParams(window.location.search).get("new") === "1";
    } catch (e) {
      return false;
    }
  })();
  const [showAllSessions, setShowAllSessions] = useState(false);
  // Native v2 Image Ad Studio (issue #31). Local, unpersisted state, same as
  // `showAllSessions` above — this screen has no session-like id to put in
  // the URL (`ImageAdStudio`'s brief/job state is ephemeral React state, not
  // loaded by id, in the v1 route either), so it does not need the `?session=`
  // machinery `useSessionSelection` gives the Autonomous/Custom flows.
  const [showImageAdStudio, setShowImageAdStudio] = useState(false);
  const [showWorkspace, setShowWorkspace] = useState(false);
  // The Creative Analyzer (issue #199). Local state for the same reason
  // `showImageAdStudio` is: the analyzer holds no id-loadable object — its
  // state is the file on the operator's disk plus a typed name — so there is
  // nothing for `?session=` to carry, here or in v1's route.
  const [showAnalyzer, setShowAnalyzer] = useState(false);

  const sessions = useEndpoint(
    () => api.listCreativeSessions(selectedId),
    [selectedId],
    { skip: !selectedId }
  );

  // ── THE RULING, ENFORCED HERE ──────────────────────────────────────────
  // Founder, 2026-09-08: the Custom-session list lives on its own screen, and
  // "when someone is in the middle of a new session, they should not see
  // previous sessions on that screen at all."
  //
  // So this is an either/or with THREE arms and no overlap between them: an
  // open session, or the sessions list, or the offer dashboard. When a session
  // is open, nothing that lists sessions is even mounted — not hidden, not
  // collapsed, not rendered behind a flag. A list that is merely not displayed
  // is one CSS change away from being displayed; a list that is not mounted is
  // not.
  if (showWorkspace) {
    return (
      <div className="ws-page">
        <PageHead
          title="Creative Workspace"
          subtitle="One angle, many bodies — image, video, and autonomous in one place."
        />
        <CreativeWorkspace offerId={selectedId} onBack={() => setShowWorkspace(false)} />
      </div>
    );
  }

  if (showAnalyzer) {
    return (
      <>
        {/* NO `PageHead` HERE, and that is deliberate — verified in a browser,
            not guessed. `CreativeAnalyzer` renders its own <h1> plus its own
            Pre-Launch/AFFILIATE badges and description, so adding the tab's
            standard page head put TWO "Creative Analyzer" <h1>s on the screen
            and said the same thing twice. The component owns its header on
            both surfaces, exactly as it does at v1's `/analyzer`.

            The back affordance lives out here, not in the component, so
            `CreativeAnalyzer.jsx` stays byte-identical for v1. Same shape as
            the Custom Creative Sessions screen below. */}
        <div className="ws-session-screen-actions">
          <button
            type="button"
            className="btn-ghost btn-sm"
            onClick={() => setShowAnalyzer(false)}
            data-testid="ws-creative-analyzer-back"
          >
            ← Ads &amp; Creative
          </button>
        </div>
        <div data-testid="ws-creative-analyzer-embed">
          <Suspense
            fallback={
              <div className="state ws-state" role="status">
                Loading the Creative Analyzer…
              </div>
            }
          >
            <CreativeAnalyzer />
          </Suspense>
        </div>
      </>
    );
  }

  if (showImageAdStudio) {
    return (
      <>
        <PageHead
          title="Image Ads"
          subtitle={
            selected
              ? `Generate an image ad for ${selected.name || "this offer"}.`
              : "Generate an image ad for this offer."
          }
        />
        <ImageAdStudio offerId={selectedId} onBack={() => setShowImageAdStudio(false)} />
      </>
    );
  }

  if (sessionId) {
    // WHICH KIND OF SESSION. The two live in different collections
    // (`custom_creative_sessions` vs `creative_sessions`) and their ids carry
    // that: a Custom session is `ccs_…`, an Autonomous one is `cs_…`. Reading
    // the prefix beats a second round-trip to ask, and beats a `?kind=` the
    // operator's URL would have to carry correctly.
    const isCustom = String(sessionId).startsWith("ccs_");
    return (
      <>
        <PageHead
          title={isCustom ? "Custom Creative Session" : "Creative Session"}
          subtitle="One session, open. Close it to return to the list."
        />
        {isCustom ? (
          <CustomSessionScreen sessionId={sessionId} onClose={close} />
        ) : (
          <AutonomousSessionScreen
            sessionId={sessionId}
            offerId={selectedId}
            isNewSession={isNew}
            onClose={close}
          />
        )}
      </>
    );
  }

  if (showAllSessions) {
    return (
      <>
        <PageHead
          title="Custom Creative Sessions"
          subtitle="Every Custom session you have started."
        />
        <div className="ws-session-screen-actions">
          <button
            type="button"
            className="btn-ghost btn-sm"
            onClick={() => setShowAllSessions(false)}
            data-testid="ws-custom-sessions-back"
          >
            ← Ads &amp; Creative
          </button>
        </div>
        <CustomSessions offerId={selectedId} onOpen={open} />
      </>
    );
  }

  return (
    <>
      <PageHead
        title="Ads & Creative"
        subtitle={
          selected
            ? `Every creative for ${selected.name || "this offer"}, and what it is doing.`
            : "Every creative for this offer, and what it is doing."
        }
      />

      <OfferPicker
        offers={offers}
        selectedId={selectedId}
        onSelect={select}
        error={error}
      />

      {/* The way IN to the sessions screen. Deliberately a link to a screen
          rather than a panel of rows on this one: Custom sessions are not
          offer-scoped in the way this dashboard is, and the whole reason the
          screen exists is finding a session you lost — which scoping to the
          currently-selected offer would defeat. */}
      <div className="ws-session-screen-actions">
        <button
          type="button"
          className="btn-ghost btn-sm"
          onClick={() => setShowAllSessions(true)}
          data-testid="ws-open-custom-sessions"
        >
          Custom Creative Sessions →
        </button>
        {/* Outside the offer-scoped grid on purpose: the analyzer picks its own
            AFFILIATE offer from the asset name, so it stays reachable before an
            offer is selected here. */}
        <button
          type="button"
          className="btn-ghost btn-sm"
          onClick={() => setShowAnalyzer(true)}
          data-testid="ws-open-creative-analyzer"
        >
          Creative Analyzer →
        </button>
      </div>

      {!selectedId ? (
        <div className="state ws-state" role="status">
          Select an offer to see its ads and creative.
        </div>
      ) : (
        <div className="ws-analytics-grid">
          <Productions offerId={selectedId} />
          <Sessions
            offerId={selectedId}
            sessions={sessions}
            onOpenImageAds={() => setShowImageAdStudio(true)}
            onOpenWorkspace={() => setShowWorkspace(true)}
          />
          <EngineStats />
        </div>
      )}
    </>
  );
}
